/**
 * What a stored value is CALLED on screen.
 *
 * WHY THIS FILE EXISTS
 *
 * Four screens were rendering machine values straight into the page: the
 * editor printed `draft`, both it and the queue printed
 * `creditCardTransaction`, the party badge printed `vendor`, and the rules
 * table printed `contains`. The operator reads title case everywhere else in
 * this application, and a lowercase database value in a list of labelled facts
 * reads as something the screen forgot to finish.
 *
 * It is one file for the reason `money.ts` is one file, and that header says
 * it plainly: `formatCents` had been written twice and the clearings screen
 * would have been the third. Two copies of a display rule drift, produce no
 * error, and show the operator the same value written two ways on two screens.
 *
 * THE DISPLAY LAYER IS THE ONLY LAYER THAT MAY DO THIS
 *
 * Nothing here is ever sent back to the API, stored, or compared against a
 * stored value. `mercury_kind` in particular is stored VERBATIM on purpose
 * (migration 0023): it records what the bank said, and a normalised copy would
 * make a value Mercury never sent indistinguishable from one it did. These
 * functions run at the point of use, where the mapping is visible.
 */

import type { FilingStatus, JournalStatus } from '../api.js';

/**
 * `creditCardTransaction` -> `Credit Card Transaction`.
 *
 * IT MUST SURVIVE VALUES NOBODY HAS SEEN. Migration 0023 deliberately puts no
 * check on the set of Mercury kinds, because Mercury has extended its enums
 * before — `reversed` and `blocked` postdate the original spec. So this is a
 * transformation, not a lookup table: a value invented in 2027 gets title-cased
 * like any other rather than falling through to blank.
 *
 * Digits start their own word, so `someRail2027` reads `Some Rail 2027` rather
 * than `Some Rail2027`. Runs of capitals are left whole, so an `ACH` that ever
 * appears does not become `A C H`.
 */
export function camelToTitle(value: string): string {
  return (
    value
      .trim()
      // lower|digit -> upper, and the last capital of a run before a lowercase
      // word: `creditCard` and `ACHCredit` both split correctly.
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // A digit run is its own word.
      .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
      // Underscores and hyphens are word breaks too, in case a value arrives
      // snake_cased.
      .replace(/[_-]+/g, ' ')
      .split(/\s+/)
      .filter((word) => word !== '')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

/** The em dash every screen uses for a value that was never captured. */
export const NOT_CAPTURED = '—';

/**
 * §9.1 — Mercury's payment kind, for display.
 *
 * Null is NOT CAPTURED rather than "no rail": every manual header, and the
 * posted headers that predate migration 0023 and can never be backfilled,
 * because the bank's facts on a posted header are refused even since 0027
 * made the rest of it editable.
 */
export function kindLabel(kind: string | null | undefined): string {
  if (kind == null || kind.trim() === '') return NOT_CAPTURED;
  return camelToTitle(kind);
}

/**
 * What the operator calls an entry's state, which is NOT what `status` stores.
 *
 * MOVED HERE FROM TriagePage ON 2026-09-11, unchanged. The editor was printing
 * the raw column while the queue used this, so the same entry was `draft` on
 * one screen and `Uncategorized` on the other.
 *
 * `draft` covers two things the operator thinks of as unrelated: bank
 * transactions nobody has coded, and entries fully coded and waiting to be
 * posted. Calling both "Draft" made the second invisible — the seeded invoices
 * could only be found by searching a counterparty name.
 *
 * So the line count is part of the question, and that is why this takes a row
 * rather than a status.
 */
export function statusLabel(row: { status: JournalStatus; lineCount: number }): string {
  if (row.status === 'posted') return 'Posted';
  if (row.status === 'dismissed') return 'Excluded';
  return row.lineCount > 0 ? 'Draft' : 'Uncategorized';
}

/** §9.7 — a coding rule's match type, for the rules table. */
export function matchTypeLabel(matchType: string): string {
  return camelToTitle(matchType);
}

/**
 * §7 — the roles a party holds, for the badge beside the picker.
 *
 * Returns null when the party holds NONE, rather than a string saying so. The
 * caller renders that case without badge chrome: a badge is a container for a
 * value, and putting one around "no role set" asserts a value where there is
 * not one. Sentence case there, not title case, because it is a remark rather
 * than a label.
 */
export function roleLabels(party: {
  isCustomer?: boolean;
  isVendor?: boolean;
  isEmployee?: boolean;
  isCharity?: boolean;
  isDonor?: boolean;
}): string | null {
  const roles = [
    party.isCustomer && 'Customer',
    party.isVendor && 'Vendor',
    party.isEmployee && 'Employee',
    party.isCharity && 'Charity',
    party.isDonor && 'Donor',
  ].filter((role): role is string => typeof role === 'string');

  return roles.length > 0 ? roles.join(', ') : null;
}

/**
 * §18.3 — a W-4 filing status, in Step 1(c)'s own words.
 *
 * The form's wording in title case, not a shortening of it: the operator reads
 * the screen against the paper, and "Single" where the form says "Single or
 * Married filing separately" would look like a different answer.
 */
export function filingStatusLabel(status: FilingStatus): string {
  switch (status) {
    case 'single_or_married_separately':
      return 'Single or Married Filing Separately';
    case 'married_jointly_or_surviving_spouse':
      return 'Married Filing Jointly or Qualifying Surviving Spouse';
    case 'head_of_household':
      return 'Head of Household';
  }
}

/**
 * An SSN on screen: its last four digits and nothing else.
 *
 * The API never sends more than the four, so this cannot show the rest. It
 * exists so every screen masks the same way.
 */
export function maskSsn(lastFour: string): string {
  return `•••-••-${lastFour}`;
}

/** A legal name on one line: first, middle, last and suffix, whichever are on file. */
export function employeeName(employee: {
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
}): string {
  return [employee.firstName, employee.middleName, employee.lastName, employee.suffix]
    .filter((part): part is string => part !== null && part.trim() !== '')
    .join(' ');
}

/**
 * §18.4 — how a pay frequency reads after an amount: "$25,000.00 each quarter".
 *
 * A transformation rather than camelToTitle, because "Biweekly" is a label and
 * "each two weeks" is a sentence, and this is used in sentences.
 */
export function periodWord(frequency: string): string {
  if (frequency === 'weekly') return 'week';
  if (frequency === 'biweekly') return 'two weeks';
  if (frequency === 'monthly') return 'month';
  if (frequency === 'quarterly') return 'quarter';
  return frequency;
}

/** An address as the lines an envelope has, leaving out what is not on file. */
export function addressLines(employee: {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}): string[] {
  const stateAndZip = [employee.state, employee.postalCode].filter(Boolean).join(' ');
  const lastLine = [employee.city, stateAndZip].filter(Boolean).join(', ');
  return [employee.addressLine1, employee.addressLine2, lastLine].filter(
    (line): line is string => line !== null && line !== '',
  );
}

/**
 * How an employee's year was made up, for a sentence: the pay entered by hand
 * before the first run, and the posted runs. The year to date and the W-2/W-3
 * worksheet both say it under their tables.
 */
export function payCounted(source: { openingThrough: string | null; runs: number }): string {
  const runs = source.runs === 0 ? 'no posted runs' : source.runs === 1 ? 'one posted run' : `${source.runs} posted runs`;
  return source.openingThrough === null ? runs : `pay entered by hand through ${source.openingThrough}, and ${runs}`;
}
